import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import {
  login as loginAction,
  logout as logoutAction,
  getUser as getUserAction,
  changeSessionOrganization as changeSessionOrganizationAction,
  updateUserOrganization as updateUserOrganizationAction,
  changePassword as changePasswordAction,
  activate as activateAction,
  forgotPassword as forgotPasswordAction,
  resetPassword as resetPasswordAction,
  validateToken as validateTokenAction
} from '../redux/slices/authSlice';

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const state = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const login = useCallback(async (data) => {
    return unwrapResult(await dispatch(loginAction(data)));
  }, []);

  const logout = useCallback(async () => {
    dispatch(logoutAction());
  }, []);

  const updateUserOrganization = useCallback((data) => {
    dispatch(updateUserOrganizationAction(data));
  }, []);

  const changeSessionOrganization = useCallback(async (organizationId) => {
    return unwrapResult(await dispatch(changeSessionOrganizationAction(organizationId)));
  }, []);

  const getUser = useCallback(async () => {
    return unwrapResult(await dispatch(getUserAction()));
  }, []);

  const changePassword = useCallback(async (data) => {
    return unwrapResult(await dispatch(changePasswordAction(data)));
  }, []);

  const activate = useCallback(async (data) => {
    return unwrapResult(await dispatch(activateAction(data)));
  }, []);

  const forgotPassword = useCallback(async (data) => {
    return unwrapResult(await dispatch(forgotPasswordAction(data)));
  }, []);

  const resetPassword = useCallback(async (data) => {
    return unwrapResult(await dispatch(resetPasswordAction(data)));
  }, []);

  const validateToken = useCallback(async (data) => {
    return unwrapResult(await dispatch(validateTokenAction(data)));
  }, []);

  const selectedOrganization = useMemo(() => {
    return state.user?.organizations?.find(
      (t) => t.id === state.user?.selectedOrganization
    );
  }, [state.user?.organizations, state.user?.selectedOrganization]);

  const roles = useMemo(() => {
    return new Set([
      ...(state.user?.roles || []),
      ...(selectedOrganization?.roles || [])
    ].map(r => r.key));
  }, [state.user?.roles, selectedOrganization?.roles]);

  const hasRole = (...roleKeys) => {
    if(roles.has('root')) {
      return true;
    }

    return roleKeys.some((key) => roles.has(key));   

  }

  useEffect(() => {
    getUser().catch(() => null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        roles: roles,
        loading: state.loading,
        isLoggedIn: state.isLoggedIn,
        selectedOrganization,
        login,
        logout,
        getUser,
        changeSessionOrganization,
        updateUserOrganization,
        changePassword,
        activate,
        forgotPassword,
        resetPassword,
        validateToken,
        hasRole
      }}>
      {children}
    </AuthContext.Provider>
  );
}